import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { textColor, THEME_BLACK } from '../../../styles/basics/colors';
import { flex } from '../../../styles/utilities/flex';
import { MATERIAL_ICON_EDIT, MATERIAL_ICON_UPLOAD } from '../../../shared/common/Button/Icon';
import { HelpTooltip } from '../../../shared/common/HelpTooltip';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';

const StyledWrapper = styled.div`
    ${flex('row')};
    justify-content: space-between;
    align-items: center;
`;

const StWrapperTitle = styled.div`
    ${flex('row')};
    align-items: center;
    font-size: 18px;
`;

const StyledTitle = styled.h3`
    ${textColor(THEME_BLACK)};
    font-size: inherit;
    font-weight: normal;
    margin-top: 0;
    margin-bottom: 0;
    margin-right: 5px;
    line-height: 1;
`;

StyledTitle.displayName = 'LCProductDetailAssetsButton__StyledTitle';

const StDeleteButton = styled(Button)`
    margin-right: 10px;
`;

const StButtonContainer = styled.div.attrs({ 'data-displayname': 'ButtonContainer' })`
    display: flex;
    align-content: flex-end;
`;

const StTooltip = styled(HelpTooltip)<{ width: number }>`
    line-height: 1;
`;

type Role = 'fileUpload' | 'edit';

interface Props {
    name: string;
    buttonText: string;
    tooltipText?: string;
    role?: Role;
    onDelete?: () => void;
    showDeleteButton?: boolean;
    onClick?: () => void;
    children?: any;
    readOnly: boolean;
    disabled?: boolean;
}

export const LCProductDetailAssetsButton: FC<Props> = ({
    name = null,
    buttonText = '',
    role = null,
    showDeleteButton = false,
    onDelete = () => {},
    tooltipText,
    readOnly,
    onClick = () => {},
    disabled,
    children
}) => {
    let icon: string = '';
    if (role === 'edit') {
        icon = MATERIAL_ICON_EDIT;
    } else if (role === 'fileUpload') {
        icon = MATERIAL_ICON_UPLOAD;
    }

    let tooltip: ReactNode;
    let deleteButton: ReactNode;
    let editButton: ReactNode;

    if (!readOnly) {
        tooltip = tooltipText ? <StTooltip text={tooltipText} width={50} /> : null;

        deleteButton = showDeleteButton ? (
            <StDeleteButton
                icon="delete"
                onClick={onDelete}
                size={ButtonSize.LARGE}
                theme={ButtonTheme.POSITIVE_INVERT}
            />
        ) : null;

        editButton = (
            <Button
                text={buttonText}
                icon={icon}
                onClick={onClick}
                size={ButtonSize.LARGE}
                theme={ButtonTheme.POSITIVE_INVERT}
                disabled={disabled}
            />
        );
    }

    return (
        <StyledWrapper>
            <StWrapperTitle>
                <StyledTitle>{name}</StyledTitle>
                {tooltip}
            </StWrapperTitle>
            <StButtonContainer>
                {deleteButton}
                {editButton}
                {children}
            </StButtonContainer>
        </StyledWrapper>
    );
};

export default LCProductDetailAssetsButton;
